// src/components/note/note.component.js
import React, { Component } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled, { createGlobalStyle } from "styled-components";
import ReactGA from "react-ga4";

const GlobalStyle = createGlobalStyle`
  body {
    padding: 0;
    margin: 0;
    align-items: center;
    background: linear-gradient(45deg, #1d9bf0, #ff0080, #4c00ff, #ff7b00);
    background-size: 400% 400%;
    background-attachment: fixed;
    animation: gradientRotate 15s ease infinite;
  }

  @keyframes gradientRotate {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .p-note {
    word-break: break-word;
    overflow-wrap: break-word;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
`;

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;
  flex-wrap: wrap;
  padding-top: 10px;
  width: 100%;
  max-width: 800px;
`;

const NoteCard = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  padding: 20px;
  margin: 20px;
  width: 90%;
  max-width: 600px;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const NoteTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: center;
`;

const NoteDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const JoinButton = styled.button`
  background-color: #007df4;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  margin-top: 6px;

  &:hover {
    background-color: #0068cc;
  }
`;

class Note extends Component {
  constructor(props) {
    super(props);

    const noteId = this.props.match.params.id;

    this.state = {
      loading: true,
      error: null,
      activeImageIndex: 0,
      note: {
        id: noteId || "default",
        title: "Fitness Note",
        description:
          "Join Bealyo to connect with people and build habits together.",
        imageUrl: "https://www.bealyo.com/images/prebealyoshare.png",
        images: [], // ✅ NEW: full images array
        createdDate: null,
        author: "",
        nickname: "Bealyo user",
        privacy: "public",
        noteValue: null,
        distance: null,
        duration: null,
        coordinates: null,
      },
      showMap: true,
    };
  }

  buildImgUrl = (path) => {
    if (!path) return null;
    // already absolute
    if (
      typeof path === "string" &&
      (path.startsWith("http://") || path.startsWith("https://"))
    ) {
      return path;
    }
    return (process.env.REACT_APP_IMG_FOLDER || "") + path;
  };

  // Keep: used by map init
  componentDidUpdate(prevProps, prevState) {
    const prevCoords = prevState.note.coordinates;
    const nextCoords = this.state.note.coordinates;

    const coordsChanged = prevCoords !== nextCoords;
    if (nextCoords && coordsChanged) {
      this.initializeMap();
    }
  }

  componentDidMount() {
    const noteId = this.props.match.params.id;

    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    axios
      .post(`${process.env.REACT_APP_API_URL}notes/pub/byid/`, {
        note_id: noteId,
      })
      .then((response) => {
        const noteArray = Array.isArray(response.data) ? response.data : [];
        const noteData = noteArray[0];

        if (!noteData) {
          this.setState({
            loading: false,
            error: "Note not found or not public.",
          });
          return;
        }

        // user_data can be [] or missing, keep it safe
        const userData = Array.isArray(noteData.user_data)
          ? noteData.user_data[0]
          : noteData.user_data;

        const nickname = userData?.nickname || "Bealyo user";
        const authorName = userData?.name || nickname;

        // Parse note_value if it exists
        let noteValue = null;
        if (noteData.note_value) {
          try {
            noteValue =
              typeof noteData.note_value === "string"
                ? JSON.parse(noteData.note_value)
                : noteData.note_value;
          } catch (e) {
            console.error("Error parsing note_value:", e);
          }
        }

        // ✅ Support new note_images array (fallback to note_image, then user img, then default)
        const noteImagesRaw = Array.isArray(noteData.note_images)
          ? noteData.note_images.filter(Boolean)
          : [];

        const legacyNoteImage = noteData.note_image
          ? [noteData.note_image]
          : [];
        const combined = [...noteImagesRaw, ...legacyNoteImage];

        // de-dupe
        const uniqueImages = Array.from(new Set(combined)).filter(Boolean);

        // Build absolute urls
        const images = uniqueImages.map(this.buildImgUrl);

        // Choose hero image / OG image
        const heroImage =
          images[0] ||
          (userData?.img_url ? this.buildImgUrl(userData.img_url) : null) ||
          "https://www.bealyo.com/images/prebealyoshare.png";

        this.setState({
          loading: false,
          error: null,
          activeImageIndex: 0,
          note: {
            id: noteData._id,
            title: `${nickname}'s Activity`,
            description: noteData.note_body || "",
            imageUrl: heroImage, // used by OG and fallback
            images, // ✅ full gallery
            createdDate: noteData.created_date
              ? new Date(noteData.created_date).toLocaleDateString()
              : null,
            author: authorName,
            nickname,
            privacy: noteData.note_privacy || "public",
            noteValue,
            distance: noteValue?.distance ?? null,
            duration: noteValue?.duration ?? null,
            coordinates: noteValue?.coordinates ?? null,
          },
        });
      })
      .catch((error) => {
        console.error("Error fetching note:", error);
        this.setState({
          error: "Failed to load note details",
          loading: false,
        });
      });
  }

  initializeMap = () => {
    const { coordinates } = this.state.note;
    if (!coordinates || coordinates.length === 0 || !window.L) return;

    // prevent re-initializing
    const existing = window.L.DomUtil.get("trackMap");
    if (existing && existing._leaflet_id) return;

    const map = window.L.map("trackMap").setView(
      [coordinates[0].latitude, coordinates[0].longitude],
      15,
    );

    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    const latlngs = coordinates.map((coord) => [
      coord.latitude,
      coord.longitude,
    ]);
    const polyline = window.L.polyline(latlngs, {
      color: "#1D9BF0",
      weight: 3,
    }).addTo(map);

    map.fitBounds(polyline.getBounds());

    window.L.marker([coordinates[0].latitude, coordinates[0].longitude])
      .addTo(map)
      .bindPopup("Start");

    window.L.marker([
      coordinates[coordinates.length - 1].latitude,
      coordinates[coordinates.length - 1].longitude,
    ])
      .addTo(map)
      .bindPopup("End");
  };

  setActiveImage = (idx) => {
    this.setState({ activeImageIndex: idx });
  };

  renderImageGallery() {
    const { note, activeImageIndex } = this.state;
    const images = Array.isArray(note.images) ? note.images : [];
    if (!images.length) return null;

    const activeSrc = images[Math.min(activeImageIndex, images.length - 1)];

    return (
      <div style={{ margin: "15px 0" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "260px",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <img
            src={activeSrc}
            alt="Note"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "55%",
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.70) 100%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "12px",
              left: "14px",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            Consistency beats motivation.
          </div>
        </div>

        {images.length > 1 && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "10px",
              overflowX: "auto",
              paddingBottom: "4px",
            }}
          >
            {images.map((src, idx) => (
              <div
                key={src + idx}
                onClick={() => this.setActiveImage(idx)}
                style={{
                  width: "70px",
                  height: "50px",
                  borderRadius: "10px",
                  overflow: "hidden",
                  cursor: "pointer",
                  border:
                    idx === activeImageIndex
                      ? "2px solid #1d9bf0"
                      : "1px solid rgba(255,255,255,0.15)",
                  flex: "0 0 auto",
                  opacity: idx === activeImageIndex ? 1 : 0.85,
                }}
                title={`Image ${idx + 1}`}
              >
                <img
                  src={src}
                  alt={`thumb-${idx}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { note, loading, error } = this.state;

    const shareUrl = `https://www.bealyo.com/notes/${note.id}`;
    const distanceLabel =
      typeof note.distance === "number"
        ? `${note.distance.toFixed(2)} km`
        : null;

    const durationLabel =
      typeof note.duration === "number"
        ? `${Math.floor(note.duration / 60)}:${(note.duration % 60)
            .toString()
            .padStart(2, "0")}`
        : null;

    const avgPaceLabel =
      note.distance && note.duration
        ? `${(note.duration / 60 / note.distance).toFixed(2)} min/km`
        : null;

    const activityHeadline = distanceLabel
      ? `Just finished a ${distanceLabel} challenge`
      : "Just finished a Bealyo challenge";

    const ogTitle = distanceLabel
      ? `${distanceLabel} Route · ${note.nickname} checked in`
      : `${note.nickname} shared a progress update`;

    const ogDescription =
      distanceLabel && durationLabel
        ? `See the route map (${distanceLabel}, ${durationLabel}) on Bealyo. Android Open Testing is live.`
        : `View this progress update on Bealyo. Android Open Testing is live.`;

    // ✅ OG image should be the first image if exists
    const ogImage =
      (Array.isArray(note.images) && note.images[0]) ||
      note.imageUrl ||
      "https://www.bealyo.com/images/prebealyoshare.png";

    return (
      <PageContainer>
        <Helmet>
          <title>{note.title} | Bealyo</title>
          <meta
            name="description"
            content={note.description || ogDescription}
          />

          {/* App Links for Android */}
          <meta property="al:android:package" content="com.bealyo.bealyoapp" />
          <meta
            property="al:android:url"
            content={`bealyo-app://note/${note.id}`}
          />
          <meta property="al:android:app_name" content="Bealyo" />

          {/* iOS App Links */}
          <meta
            property="al:ios:url"
            content={`bealyo-app://note/${note.id}`}
          />
          <meta property="al:ios:app_store_id" content="YOUR_APP_STORE_ID" />
          <meta property="al:ios:app_name" content="Bealyo" />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={ogImage} />

          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={shareUrl} />
          <meta property="twitter:title" content={ogTitle} />
          <meta property="twitter:description" content={ogDescription} />
          <meta property="twitter:image" content={ogImage} />
        </Helmet>

        <GlobalStyle />
        <CenteredContainer>
          <NoteCard>
            {loading && (
              <div
                style={{ textAlign: "center", padding: "18px 0", opacity: 0.9 }}
              >
                Loading note...
              </div>
            )}

            {!loading && error && (
              <div style={{ textAlign: "center", padding: "18px 0" }}>
                <div style={{ fontWeight: 800, fontSize: 18 }}>
                  Couldn’t load this note
                </div>
                <div style={{ marginTop: 8, opacity: 0.9 }}>{error}</div>
              </div>
            )}

            {!loading && !error && (
              <>
                <NoteTitle>{note.title}</NoteTitle>

                <div style={{ margin: "10px 0", textAlign: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: "18px" }}>
                    Real people building habits together on Bealyo
                  </div>
                  <div style={{ fontSize: "13px", opacity: 0.85 }}>
                    This progress was shared from Bealyo — a community-driven
                    challenge app.
                  </div>
                </div>

                {/* Map */}
                {note.coordinates && note.coordinates.length > 0 && (
                  <div style={{ margin: "15px 0", width: "100%" }}>
                    <div
                      id="trackMap"
                      style={{
                        width: "100%",
                        height: "260px",
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
                        border: "1px solid rgba(255,255,255,0.12)",
                      }}
                    />
                    <div
                      style={{
                        marginTop: "10px",
                        fontSize: "13px",
                        opacity: 0.92,
                      }}
                    >
                      {distanceLabel && (
                        <span style={{ marginRight: 10 }}>
                          📏 {distanceLabel}
                        </span>
                      )}
                      {durationLabel && <span>⏱ {durationLabel}</span>}
                    </div>
                  </div>
                )}

                {/* ✅ Images gallery (note_images + note_image) */}
                {this.renderImageGallery()}

                <NoteDescription>{activityHeadline}</NoteDescription>

                {note.description && (
                  <p
                    className="p-note"
                    style={{ marginTop: "-8px", opacity: 0.9 }}
                  >
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        opacity: 0.95,
                      }}
                    >
                      {note.description}
                    </div>
                  </p>
                )}

                <p>By: @{note.nickname}</p>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    fontSize: "13px",
                    margin: "10px 0 6px 0",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {note.createdDate && <span>🗓 {note.createdDate}</span>}
                  {distanceLabel && <span>📏 {distanceLabel}</span>}
                  {durationLabel && <span>⏱ {durationLabel}</span>}
                  {avgPaceLabel && <span>🏃 {avgPaceLabel}</span>}
                </div>

                <div
                  style={{
                    margin: "8px 0 14px 0",
                    fontSize: "13px",
                    opacity: 0.92,
                  }}
                >
                  Others are following this journey on Bealyo
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span
                    style={{
                      alignSelf: "flex-start",
                      background: "#1d9bf0",
                      color: "#fff",
                      borderRadius: "999px",
                      padding: "4px 10px",
                      fontSize: "12px",
                      fontWeight: 500,
                      letterSpacing: "0.1px",
                    }}
                  >
                    Early Access
                  </span>

                  <JoinButton
                    onClick={() =>
                      window.open(
                        "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp",
                        "_blank",
                      )
                    }
                    style={{
                      backgroundColor: "#007DF4",
                      marginTop: "2px",
                      padding: "14px 20px",
                      fontSize: "15px",
                      fontWeight: 500,
                    }}
                  >
                    Get Bealyo (Android Open Testing)
                  </JoinButton>

                  <div style={{ fontSize: "12px", opacity: 0.85 }}>
                    Available on Android (Open Testing) · iOS coming later
                  </div>
                </div>
              </>
            )}
          </NoteCard>
        </CenteredContainer>
      </PageContainer>
    );
  }
}

export default Note;
